import Axios from 'helpers/Interceptor';
import { BrandPermission, MemberRole, TeamPricesData, CurrentBrand, OrganizationData, TeamMember } from 'modules/Types'
import * as Sentry from "@sentry/react"
import { AxiosRequestConfig } from 'axios';
import { PlanType } from 'modules/Const';

interface AddNewMember {
  member: {
    name: string;
    lastName: string;
    email: string;
    role: MemberRole;
  },
  brands: CurrentBrand[]
}

interface UpdateMemberPermissions {
  addedBrands: {
    brandId: number;
    permission: BrandPermission;
  }[]
  role?: MemberRole;
  editedBrands: {
    brandId: number;
    permission: BrandPermission;
  }[]
  removedBrands: number[]
}

interface GetSimulationPrices {
  typeSimulation: 'add-seat' | 'remove-seat'
}

/**
 * Optional query parameters for fetching organization data
 * Note: If no fields are specified, this will default to only returning id
 */
interface OrganizationQueryParams {
  /** Comma-separated list of brand fields to return */
  fields?: Array<'id' | 'fanbaseSize' | 'totalCampaigns'>;
}

/**
 * Fetches main info on the organization
 * @async
 * @returns {Promise<any>} Organization members data or null if error
 */
export const getOrganization_api = async (organizationId?: number, params?: OrganizationQueryParams) => {
  try {
    let requestParams: AxiosRequestConfig = {}
    if (params?.fields) {
      requestParams.params = {
        fields: params.fields.join(',')
      }
    }
    const response = await Axios.get<OrganizationData>(`/organization/${organizationId}`, requestParams);
    if (!response?.data)
      throw new Error('Error fetching organization data');
    return response.data;
  } catch (error) {
    return null;
  }
};

/**
 * Fetches all members of the organization
 * @async
 * @returns {Promise<any>} Organization members data or null if error
 */
export const getOrganizationTeamMembers_api = async (organizationId: number) => {
  try {
    const response = await Axios.get<TeamMember[]>(`/organization/${organizationId}/members`);
    if (!response?.data)
      throw new Error('Error fetching organization members');
    return response.data;
  } catch (error) {
    return null;
  }
};


/**
 * Retrieves Stripe team plan pricing information
 * @async
 * @returns {Promise<{priceExtraSeat: number, priceExtraSeatRemaining: number, remainingDays: number}>} Team plan cost details
 * @todo Implement actual endpoint service integration
 */
export const getStripeTeamPlanCosts = async (organizationId: number) => {
  //TODO: return data from endpoint service
  return {
    priceExtraSeat: 5,
    priceExtraSeatRemaining: 5,
    remainingDays: 10,
  }
};
/**
 * Removes a member from the organization
 * @async
 * @param {number} memberId - ID of member to remove
 * @returns {Promise<any>} Response data or null if error
 */
export const removeOrganizationMember_api = async (organizationId: number, memberId: number) => {
  try {
    const response = await Axios.delete(`/organization/${organizationId}/members/${memberId}`);
    return response.status === 200;
  } catch (error) {
    return false;
  }
};

/**
 * Deletes a brand from the organization
 * @async
 * @param {number} brandId - ID of the brand to delete
 * @returns {Promise<boolean>} True if deletion was successful, false otherwise
 */
export const deleteOrganizationBrand_api = async (organizationId: number, brandId: number): Promise<boolean> => {
  try {
    const response = await Axios.delete(`/organization/${organizationId}/brands/${brandId}`);
    return response.status === 200;
  } catch (error) {
    return false;
  }
};

/**
 * Gets price simulation data for adding/removing seats
 * @async
 * @param {GetSimulationPrices} param0 - Simulation type configuration
 * @returns {Promise<TeamPricesData | null>} Price simulation data or null if error
 */
export const getSimulationPrices_api = async (organizationId: number, { typeSimulation }: GetSimulationPrices): Promise<TeamPricesData | null> => {
  try {
    const response = await Axios.get<TeamPricesData>(`/organization/${organizationId}/simulation-prices/${typeSimulation}`);
    if (!response?.data)
      throw new Error('Error fetching extra prices');
    return response.data;
  } catch (error) {
    return null;
  }
}

/**
 * Redirects to the subscription status URL
 * @async
 * @returns {Promise<string>} Subscription status URL
 * @throws {Error} If the request fails
 */
export const getSubscriptionStatusUrl_api = async (organizationId: number): Promise<string> => {
  try {
    const response = await Axios.post<{ url: string }>(`/organization/${organizationId}/customer-portal`, {});
    if (!response?.data?.url) {
      throw new Error('Error fetching subscription status URL');
    }
    return response.data.url;
  } catch (error) {
    throw error;
  }
};

/**
 * Adds a new member to the organization
 * @async
 * @param {AddNewMember} data - New member details and brand assignments
 * @returns {Promise<any>} Response data
 * @throws {Error} If member addition fails
 */
export const addNewTeamMember_api = async (organizationId: number, data: AddNewMember) => {
  try {
    const response = await Axios.post(`/organization/${organizationId}/members`, data);
    if (!response?.data)
      throw new Error('Error adding member');
    return response.data;
  } catch (error) {
    throw error;
  }
}
/**
 * Updates permissions and role for an organization team member
 * @async
 * @param {number} organizationId - ID of the organization to update
 * @param {number} memberId - ID of the team member to update
 * @param {UpdateMemberPermissions} data - Updated permissions data including:
 *   - addedArtists: Array of new artist permissions to add
 *   - editedArtists: Array of existing artist permissions to modify
 *   - removedArtists: Array of artist IDs to remove access for
 *   - role: Optional new role to assign to the member
 * @returns {Promise<TeamMember>} Updated team member object with new permissions
 * @throws {Error} If permission update fails
 */
export const updateTeamMemberPermissions_api = async (organizationId: number, memberId: number, data: UpdateMemberPermissions): Promise<TeamMember> => {
  try {
    const response = await Axios.put<TeamMember>(`/organization/${organizationId}/members/${memberId}`, data);
    if (!response?.data)
      throw new Error('Error updating member permissions');
    return response.data;
  } catch (error) {
    throw error
  }
}

/**
 * Fetches organization campaigns with pagination
 * @async
 * @param {number} organizationId - ID of the organization to fetch campaigns for
 * @param {number} offset - Pagination offset
 * @param {number} limit - Number of items per page
 * @returns {Promise<any>} Paginated campaign data or null if error
 */
export const getOrganizationCampaigns_api = async (organizationId: number, offset: number, limit: number) => {
  try {
    const params = {
      offset,
      limit
    };
    const response = await Axios.get(`/organization/${organizationId}/campaigns`, { params });
    if (!response?.data)
      throw new Error('Error fetching organization campaigns');
    return response.data;
  } catch (error) {
    return null;
  }
};

/**
 * Fetches organization websites with pagination
 * @async
 * @param {number} organizationId - ID of the organization to fetch websites for
 * @param {number} offset - Pagination offset
 * @param {number} limit - Number of items per page
 * @returns {Promise<any>} Paginated website data or null if error
 */
export const getOrganizationWebsites_api = async (organizationId: number, offset: number, limit: number) => {
  try {
    const params = {
      offset,
      limit
    };
    const response = await Axios.get(`/organization/${organizationId}/websites`, { params });
    if (!response?.data)
      throw new Error('Error fetching organization websites');
    return response.data;
  } catch (error) {
    return null;
  }
};

/**
 * Fetches organization fans with pagination and sorting
 * @async
 * @param {number} organizationId - ID of the organization to fetch fans for
 * @param {number} offset - Pagination offset
 * @param {number} limit - Number of items per page
 * @param {string} orderBy - Field to sort by (added, email, location, source, brand)
 * @param {string} direction - Sort direction (ASC or DESC)
 * @param {boolean} [targeting] - Optional targeting filter
 * @returns {Promise<OrganizationFansResponse | null>} Paginated fans data or null if error
 */
export const getOrganizationFans_api = async (
  organizationId: number,
  offset: number,
  limit: number,
  orderBy: string,
  direction: string,
  targeting?: boolean
) => {
  try {
    const params = {
      offset,
      limit,
      orderBy,
      direction,
      ...(targeting && { targeting })
    };

    const response = await Axios.get(`/organization/${organizationId}/fans`, { params });
    if (!response?.data)
      throw new Error('Error fetching organization fans');
    return response.data;
  } catch (error) {
    return null;
  }
};

/**
 * Fetches organization billing data
 * @async
 * @param {number} organizationId - ID of the organization to fetch billing data for
 * @returns {Promise<OrganizationBillingData | null>} Organization billing data or null if error
 */
export const getOrganizationBillingData_api = async (organizationId: number) => {
  try {
    const response = await Axios.get(`/organization/${organizationId}/billing`);
    if (!response?.data)
      throw new Error('Error fetching organization billing data');
    return response.data;
  } catch (error) {
    return null;
  }
};

/**
 * Completes organization setup with selected name + brand slug.
 * @param {number} organizationId - ID of the organization to complete setup for
 * @param {string} name - The name of the organization
 * @param {Array<{brandId: number, currentTier: "pro"|"free", upgradeToPro: boolean}>} brands - Array of brand profiles
 * @returns {Promise<any>} Response data
 */
export const completeOrganizationSetup_api = async (
  organizationId: number,
  name: string,
  brands: {
    brandId: number;
    currentTier: "pro" | "free";
    upgradeToPro: boolean;
  }[]
) => {
  const scope = 'organizationApi::completeOrganizationSetup_api'
  try {
    const payload = {
      name,
      brands
    }

    const response = await Axios.post(`/organization/${organizationId}`, payload);
    if (response?.isError) {
      throw new Error(`${scope}: Error completing organization setup`);
    }

    return response.data;
  } catch (error) {
    Sentry.captureException(error)
    console.error(`${scope}: Error completing organization setup`, error)
    return null;
  }
}

/** Interface for the primary owner data structure */
interface PrimaryOwner {
  id: number;
  name: string;
  email: string;
}

/** Interface for the admin create organization response */
interface AdminCreateOrganizationResponse {
  primaryOwner?: PrimaryOwner;
  organization?: OrganizationData;
}

/**
 * Creates a new organization with a trial plan. Used mainly for admin setup.
 * @param {string} name - The name of the organization to create
 * @param {string} email - The email of the organization owner
 * @param {Array<{id: number, currentTier: "pro"|"free", upgradeToPro: boolean}>} brands - Array of brands to add
 * @returns {Promise<AdminCreateOrganizationResponse>} Response containing organization data
 */
export const adminCreateNewOrganizationWithTrial_api = async (
  name: string,
  email: string,
  brands: {
    brandId: number;
    currentTier: 'pro' | 'free';
    upgradeToPro: boolean;
  }[]
): Promise<AdminCreateOrganizationResponse> => {
  try {
    const response = await Axios.post<{
      organization: OrganizationData;
      primaryOwner?: PrimaryOwner;
    }>(
      '/organization/admin/create-new-organization-with-trial',
      {
        name,
        ownerEmail: email,
        brands: brands.map(brand => ({
          brandId: brand.brandId,
          currentTier: brand.currentTier,
          upgradeToPro: brand.upgradeToPro
        }))
      }
    );

    return {
      organization: response.data.organization,
      primaryOwner: response.data.primaryOwner
    };
  } catch (error) {
    Sentry.captureException(error);
    console.error('error: ', error);
    return {};
  }
};


export interface UpdatedTierBrandResponse {
  id: number;
  tier: PlanType;
}
/**
 * Upgrades an organization's brand to Pro tier
 * @async
 * @param {number} organizationId - ID of the organization the brand belongs to
 * @param {number} brandId - The ID of the brand to upgrade
 * @param {'upgrade' | 'downgrade'} updateType - Whether to upgrade or downgrade the subscription
 * @returns {Promise<UpdatedTierBrandResponse | null>} Updated brand data or null if error
 */
export const updateTierInOrganization_api = async (organizationId: number, brandId: number, updateType: 'upgrade' | 'downgrade') => {
  try {
    const res = await Axios.post<{ brand: CurrentBrand }>(
      `/organization/${organizationId}/brands/${brandId}/update-subscription`,
      { updateType }
    );
    if (!res?.data) {
      throw new Error('Error upgrading organization brand subscription');
    }

    const resFinal: UpdatedTierBrandResponse = {
      id: res.data.brand.id,
      tier: res.data.brand.tier
    }
    return resFinal;
  } catch (error) {
    return null;
  }
};

/**
 * Fetches organization campaign usage data
 * @async
 * @param {number} organizationId - ID of the organization to fetch usage for
 * @param {number} budget - The budget amount to check usage against
 * @param {string} currency - The currency code for the budget
 * @returns {Promise<OrganizationCampaignUsage | null>} Campaign usage data or null if error
 */
export const getOrganizationCampaignUsage_api = async (
  organizationId: number,
  budget: number,
  currency: string
) => {
  try {
    const params = {
      budget,
      currency
    };
    const response = await Axios.get(`/organization/${organizationId}/campaign-usage`, { params });
    if (!response?.data)
      throw new Error('Error fetching organization campaign usage');
    return response.data;
  } catch (error) {
    return null;
  }
};

/**
 * Adds an existing brand to the user's organization
 * @async
 * @param {number} organizationId - ID of the organization to add brand to
 * @param {number} brandId - Unique ID for the brand to add
 * @param {boolean} upgradeToPro - Whether to upgrade the brand to Pro tier
 * @returns {Promise<{ brands: [{ id: number, tier: PlanType }] } | null>} Response data if successful, null if failed
 */
export const addBrandToOrganization_api = async (
  organizationId: number,
  brandId: number,
  upgradeToPro: boolean = false
): Promise<{ brands: [{ id: number, tier: PlanType }] } | null> => {
  const payload = {
    brandIds: [brandId],
    upgradeToPro,
  };

  try {
    const response = await Axios.post<{ brands: [{ id: number, tier: PlanType }] }>(
      `/organization/${organizationId}/brands`,
      payload
    );
    return response?.data || null;
  } catch (err) {
    return null;
  }
};

/**
 * Adds multiple existing brands to the user's organization
 * @async
 * @param {number} organizationId - ID of the organization to add brands to
 * @param {number[]} brandIds - Array of unique identifier slugs for the brands to add
 * @param {boolean} upgradeToPro - Whether to upgrade the brands to Pro tier
 * @returns {Promise<any>} Response data if successful, null if failed
 */
export const addBrandsToOrganization_api = async (
  organizationId: number,
  brandIds: number[],
  upgradeToPro: boolean = false
): Promise<{ brands: CurrentBrand[] } | null> => {
  const payload = {
    brandIds,
    upgradeToPro,
  };

  try {
    const response = await Axios.post<{ brands: CurrentBrand[] }>(
      `/organization/${organizationId}/brands`,
      payload
    );
    return response?.data || null;
  } catch (err) {
    return null;
  }
};

/**
 * Lists existing artists in the organization
 * @async
 * @param {number} organizationId - ID of the organization to list artists for
 * @returns {Promise<CurrentBrand[]>} Array of organization brands or empty array if error
 */
export const listExistingArtists_api = async (organizationId: number): Promise<CurrentBrand[]> => {
  try {
    const response = await Axios.get<CurrentBrand[]>(`/organization/${organizationId}/artists/existing`);
    return response?.data || [];
  } catch (err) {
    return [];
  }
};